
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlusCircle, Euro, Calendar, Zap, Activity } from "lucide-react";
import { toast } from "sonner";
import { updateActivityPrice, ActivityPrice, deactivateActivityPrice } from "@/hooks/useFinanceData";
import { supabase } from "@/integrations/supabase/client";
import { PriceFormDialog } from "./PriceFormDialog";
import { ActivityPriceCard } from "./ActivityPriceCard";

interface Activity {
  id: string;
  name: string;
}

interface ActivityPriceManagerProps {
  prices: ActivityPrice[];
  onPriceCreated: () => void;
}

export const ActivityPriceManager = ({ prices, onPriceCreated }: ActivityPriceManagerProps) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPrice, setEditingPrice] = useState<ActivityPrice | null>(null);
  const [preselectedActivity, setPreselectedActivity] = useState<string>('');
  const [preselectedBillingType, setPreselectedBillingType] = useState<'per_session' | 'monthly'>('per_session');

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      const { data, error } = await supabase
        .from('activities')
        .select('id, name')
        .eq('status', 'active');
      
      if (error) throw error;
      setActivities(data || []);
    } catch (error) {
      console.error('Error loading activities:', error);
    }
  };

  // Agrupar precios por actividad
  const pricesByActivity = prices.reduce((acc, price) => {
    const activityId = price.activity_id;
    if (!acc[activityId]) {
      acc[activityId] = { per_session: null, monthly: null };
    }
    acc[activityId][price.billing_type_target] = price;
    return acc;
  }, {} as Record<string, { per_session: ActivityPrice | null; monthly: ActivityPrice | null }>);

  const handleEdit = (price: ActivityPrice) => {
    setEditingPrice(price);
    setPreselectedActivity('');
    setPreselectedBillingType('per_session');
    setShowForm(true);
  };

  const handleCreatePrice = (activityId: string, billingType: 'per_session' | 'monthly') => {
    setEditingPrice(null);
    setPreselectedActivity(activityId);
    setPreselectedBillingType(billingType);
    setShowForm(true);
  };

  const handleDeactivate = async (priceId: string) => {
    try {
      await deactivateActivityPrice(priceId);
      toast.success("Precio desactivado correctamente");
      onPriceCreated();
    } catch (error) {
      console.error('Error al desactivar precio:', error);
      toast.error("Error al desactivar el precio");
    }
  };

  const getActivityPriceStats = () => {
    const activitiesWithPrices = new Set(prices.map(p => p.activity_id));
    const sessionPrices = prices.filter(p => p.billing_type_target === 'per_session').length;
    const monthlyPrices = prices.filter(p => p.billing_type_target === 'monthly').length;
    const completelyConfigured = Object.values(pricesByActivity).filter(
      p => p.per_session && p.monthly
    ).length;

    return {
      totalActivities: activities.length,
      activitiesWithPrices: activitiesWithPrices.size,
      sessionPrices,
      monthlyPrices,
      completelyConfigured
    };
  };

  const stats = getActivityPriceStats();

  return (
    <Card className="shadow-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <Euro className="w-5 h-5 text-primary" />
              Gestión de Precios por Actividad
            </CardTitle>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Activity className="w-3 h-3" />
                {stats.activitiesWithPrices}/{stats.totalActivities} con precios
              </span>
              <span className="flex items-center gap-1">
                <Zap className="w-3 h-3" />
                {stats.sessionPrices} por día
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {stats.monthlyPrices} mensuales
              </span>
              <Badge variant="outline" className="text-xs">
                {stats.completelyConfigured} completas
              </Badge>
            </div>
          </div>
          <Button 
            onClick={() => {
              setEditingPrice(null);
              setPreselectedActivity('');
              setPreselectedBillingType('per_session');
              setShowForm(true);
            }}
            size="sm"
            className="flex items-center gap-2"
          >
            <PlusCircle className="w-4 h-4" />
            Nuevo Precio
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs defaultValue="by-activity" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="by-activity">Por Actividad</TabsTrigger>
            <TabsTrigger value="list">Lista de Precios</TabsTrigger>
          </TabsList>

          <TabsContent value="by-activity" className="space-y-4">
            {activities.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay actividades disponibles</p>
                <p className="text-sm">Crea una actividad primero para configurar precios</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activities.map((activity) => {
                  const activityPrices = pricesByActivity[activity.id] || { per_session: null, monthly: null };
                  return (
                    <ActivityPriceCard
                      key={activity.id}
                      activity={activity}
                      sessionPrice={activityPrices.per_session}
                      monthlyPrice={activityPrices.monthly}
                      onEditPrice={handleEdit}
                      onCreatePrice={handleCreatePrice}
                      onDeactivatePrice={handleDeactivate}
                    />
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="list" className="space-y-4">
            {prices.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Euro className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay precios configurados</p>
                <p className="text-sm">Crea el primer precio para empezar</p>
              </div>
            ) : (
              <div className="space-y-2">
                {prices.map((price) => {
                  const activity = activities.find(a => a.id === price.activity_id);
                  return (
                    <div key={price.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="font-medium">{activity?.name || 'Actividad desconocida'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge 
                              variant={price.billing_type_target === 'per_session' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {price.billing_type_target === 'per_session' ? (
                                <><Zap className="w-3 h-3 mr-1" />Por día</>
                              ) : (
                                <><Calendar className="w-3 h-3 mr-1" />Mensual</>
                              )}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {price.price_amount}€
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={price.is_active ? 'default' : 'secondary'}>
                          {price.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                        <Button size="sm" variant="outline" onClick={() => handleEdit(price)}>
                          Editar
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <PriceFormDialog
          open={showForm}
          onOpenChange={setShowForm}
          activities={activities}
          editingPrice={editingPrice}
          onSuccess={onPriceCreated}
          preselectedActivity={preselectedActivity}
          preselectedBillingType={preselectedBillingType}
        />
      </CardContent>
    </Card>
  );
};
